import type { RequiredGeneric } from '@pba/react-utils';
import type { InvitationTokenKind, ProgramEventInviteeRole } from 'models';
import { useTranslation } from 'react-i18next';
import { routes } from 'routes/routes';
import { AvatarList, Button, Col, Form, Title as Heading, Icon, Row, Space, message } from 'ui';
import { copyToClipboard } from 'features/copy-to-clipboard';
import { useGenerateInviteTokenMutation } from '../../../queries';
import type { CommonFormValues } from '../../event-form-modal';

type InviteOptions = {
  eventId: string;
  kind: InvitationTokenKind;
  successMessage: string;
};

type P = {
  buttonText: string;
  disabled?: boolean;
  inviteOptions?: InviteOptions;
  label: string;
  onPress: () => void;
  roleType: ProgramEventInviteeRole;
};

const EventInviteesSelect = <FormValues extends CommonFormValues = never>({
  buttonText,
  disabled,
  inviteOptions,
  label,
  onPress,
  roleType,
}: RequiredGeneric<FormValues, P>) => {
  const { t } = useTranslation();
  const { isPending: isGeneratingInviteToken, mutateAsync: generateInviteToken } = useGenerateInviteTokenMutation();
  const onGenerateInviteLink = async ({ eventId, kind, successMessage }: InviteOptions) => {
    await copyToClipboard(async () => {
      const tokenData = await generateInviteToken({ kind, subjectId: eventId });

      const link = `${window.location.origin}${routes.events.share(eventId, tokenData.token)}`;
      message.success(successMessage);

      return link;
    });
  };

  return (
    <Form.Item<CommonFormValues> dependencies={['invitees', 'emailInvitees']} noStyle>
      {({ getFieldValue }) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
        const normalUsers: Exclude<CommonFormValues['invitees'], undefined> = getFieldValue('invitees') ?? [];
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
        const emailUsers: Exclude<CommonFormValues['emailInvitees'], undefined> = getFieldValue('emailInvitees') ?? [];

        const allUsers = [
          ...normalUsers
            .filter((user) => user.eventRole === roleType)
            .map((user) => ({
              id: user.id,
              url: user.profilePhoto?.url,
            })),
          ...emailUsers
            .filter((user) => user.eventRole === roleType)
            .map((user) => ({
              id: user.email,
              title: user.email,
            })),
        ];

        return (
          <Row className="event_invitees_select">
            <Col span={24}>
              <Heading level={5}>{t(label)}</Heading>
            </Col>
            <Col span={24}>
              <Space size="xs">
                {inviteOptions ? (
                  <Button
                    icon={<Icon icon="link-line" />}
                    loading={isGeneratingInviteToken}
                    onClick={async () => {
                      await onGenerateInviteLink(inviteOptions);
                    }}
                  >
                    Copy link
                  </Button>
                ) : null}

                <Space size={40}>
                  <Button disabled={disabled} icon={<Icon icon="add-circle-line" />} onClick={onPress} type="secondary">
                    {t(buttonText)}
                  </Button>

                  <AvatarList titles={allUsers} />
                </Space>
              </Space>
            </Col>
          </Row>
        );
      }}
    </Form.Item>
  );
};

export default EventInviteesSelect;
